import { useEffect, useMemo, useState } from "react";
import { Loader2, Play } from "lucide-react";
import { Field, FieldGrid } from "@/components/shared/form";
import { Button } from "@/components/ui/button";
import { Input, NativeSelect } from "@/components/ui/input";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { useStockLocations } from "@/features/locations/api";
import type { ProcessPlanStage } from "@/features/planning/types";
import {
  loadPreviousStageOutputBatches,
  useCompleteStageRun,
} from "@/features/production/api";

type InputRow = {
  process_plan_input_id: string | null;
  inventory_batch_id: string;
  label: string;
  actual_qty: number;
  actual_weight: number;
  available_qty: number;
  available_weight: number;
};

type OutputRow = {
  process_plan_output_id: string | null;
  material_id: string;
  material_grade_id: string | null;
  shape_size_id: string | null;
  label: string;
  location_id: string;
  actual_qty: number;
  actual_weight: number;
  thickness_mm: number | null;
  width_mm: number | null;
  length_mm: number | null;
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  stage: ProcessPlanStage | null;
  defaultLocationId?: string | null;
};

export function ExecuteStageSheet({ open, onOpenChange, stage, defaultLocationId }: Props) {
  const { data: locations = [] } = useStockLocations();
  const complete = useCompleteStageRun();
  const [notes, setNotes] = useState("");
  const [inputs, setInputs] = useState<InputRow[]>([]);
  const [outputs, setOutputs] = useState<OutputRow[]>([]);
  const [loadingPrev, setLoadingPrev] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const activeLocations = useMemo(
    () => locations.filter((l) => l.is_active),
    [locations],
  );
  const fallbackLoc = defaultLocationId || activeLocations[0]?.id || "";

  useEffect(() => {
    if (!open || !stage) return;
    setNotes("");
    setError(null);

    setOutputs(
      (stage.outputs ?? []).map((o) => ({
        process_plan_output_id: o.id,
        material_id: o.material_id,
        material_grade_id: o.material_grade_id,
        shape_size_id: o.shape_size_id,
        label: [o.material_name, o.grade_name, o.shape_label].filter(Boolean).join(" · "),
        location_id: fallbackLoc,
        actual_qty: o.planned_qty,
        actual_weight: o.planned_weight,
        thickness_mm: o.thickness_mm,
        width_mm: o.width_mm,
        length_mm: o.length_mm,
      })),
    );

    if (stage.input_source === "inventory") {
      setInputs(
        (stage.inputs ?? []).map((i) => ({
          process_plan_input_id: i.id,
          inventory_batch_id: i.inventory_batch_id,
          label: [i.batch_no, i.material_name].filter(Boolean).join(" · "),
          actual_qty: i.planned_qty,
          actual_weight: i.planned_weight,
          available_qty: i.planned_qty,
          available_weight: i.planned_weight,
        })),
      );
      return;
    }

    if (!stage.previous_stage_id) {
      setInputs([]);
      return;
    }

    setLoadingPrev(true);
    void loadPreviousStageOutputBatches(stage.previous_stage_id)
      .then((rows) => {
        setInputs(
          rows.map((r) => ({
            process_plan_input_id: null,
            inventory_batch_id: r.inventory_batch_id,
            label: [r.batch_no, r.material_name].filter(Boolean).join(" · "),
            actual_qty: r.available_qty,
            actual_weight: r.available_weight,
            available_qty: r.available_qty,
            available_weight: r.available_weight,
          })),
        );
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoadingPrev(false));
  }, [open, stage, fallbackLoc]);

  async function onSubmit() {
    if (!stage) return;
    setError(null);
    try {
      await complete.mutateAsync({
        stageId: stage.id,
        notes,
        inputs: inputs.map((i) => ({
          process_plan_input_id: i.process_plan_input_id,
          inventory_batch_id: i.inventory_batch_id,
          actual_qty: i.actual_qty,
          actual_weight: i.actual_weight,
        })),
        outputs: outputs.map((o) => ({
          process_plan_output_id: o.process_plan_output_id,
          material_id: o.material_id,
          material_grade_id: o.material_grade_id,
          shape_size_id: o.shape_size_id,
          location_id: o.location_id,
          actual_qty: o.actual_qty,
          actual_weight: o.actual_weight,
          thickness_mm: o.thickness_mm,
          width_mm: o.width_mm,
          length_mm: o.length_mm,
        })),
      });
      onOpenChange(false);
    } catch {
      /* toast from mutation */
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-lg">
        <SheetHeader>
          <SheetTitle>
            Run stage {stage?.stage_index}
            {stage?.process_name ? ` · ${stage.process_name}` : ""}
          </SheetTitle>
          <SheetDescription>
            Consume inputs and create output batches. QC holds deferred to 6.4.
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 space-y-3 overflow-y-auto p-4">
          {error ? <p className="text-xs text-destructive">{error}</p> : null}
          {loadingPrev ? (
            <p className="text-xs text-muted-foreground">Loading previous outputs…</p>
          ) : null}

          {stage?.input_source === "previous_stage" && !inputs.length && !loadingPrev ? (
            <p className="text-xs text-destructive">
              Complete the previous stage first (no in-stock outputs found).
            </p>
          ) : null}

          {inputs.length > 0 ? (
            <div className="space-y-2">
              <div className="text-2xs font-semibold uppercase tracking-wide text-muted-foreground">
                Consume inputs
              </div>
              {inputs.map((row, i) => (
                <div key={row.inventory_batch_id} className="rounded border border-border p-2">
                  <div className="mb-1 font-mono text-2xs">{row.label}</div>
                  <FieldGrid>
                    <Field label="Qty" span={6}>
                      <Input
                        type="number"
                        step="0.001"
                        value={row.actual_qty}
                        onChange={(e) => {
                          const next = [...inputs];
                          next[i] = { ...row, actual_qty: Number(e.target.value) || 0 };
                          setInputs(next);
                        }}
                      />
                    </Field>
                    <Field label="Weight" span={6}>
                      <Input
                        type="number"
                        step="0.001"
                        value={row.actual_weight}
                        onChange={(e) => {
                          const next = [...inputs];
                          next[i] = { ...row, actual_weight: Number(e.target.value) || 0 };
                          setInputs(next);
                        }}
                      />
                    </Field>
                  </FieldGrid>
                </div>
              ))}
            </div>
          ) : null}

          <div className="space-y-2">
            <div className="text-2xs font-semibold uppercase tracking-wide text-muted-foreground">
              Produce outputs
            </div>
            {outputs.map((row, i) => (
              <div key={row.process_plan_output_id ?? i} className="rounded border border-border p-2">
                <div className="mb-1 text-xs font-medium">{row.label || "Output"}</div>
                <FieldGrid>
                  <Field label="Location" span={12}>
                    <NativeSelect
                      value={row.location_id}
                      onChange={(e) => {
                        const next = [...outputs];
                        next[i] = { ...row, location_id: e.target.value };
                        setOutputs(next);
                      }}
                    >
                      <option value="">Select…</option>
                      {activeLocations.map((l) => (
                        <option key={l.id} value={l.id}>
                          {l.code} — {l.name}
                        </option>
                      ))}
                    </NativeSelect>
                  </Field>
                  <Field label="Qty" span={6}>
                    <Input
                      type="number"
                      step="0.001"
                      value={row.actual_qty}
                      onChange={(e) => {
                        const next = [...outputs];
                        next[i] = { ...row, actual_qty: Number(e.target.value) || 0 };
                        setOutputs(next);
                      }}
                    />
                  </Field>
                  <Field label="Weight" span={6}>
                    <Input
                      type="number"
                      step="0.001"
                      value={row.actual_weight}
                      onChange={(e) => {
                        const next = [...outputs];
                        next[i] = { ...row, actual_weight: Number(e.target.value) || 0 };
                        setOutputs(next);
                      }}
                    />
                  </Field>
                </FieldGrid>
              </div>
            ))}
          </div>

          <FieldGrid>
            <Field label="Notes" span={12}>
              <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
            </Field>
          </FieldGrid>
        </div>

        <SheetFooter>
          <Button type="button" variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={complete.isPending || !stage || !outputs.length}
            onClick={() => void onSubmit()}
          >
            {complete.isPending ? <Loader2 className="animate-spin" /> : <Play />}
            Complete stage
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
